import { ISelectOption } from '@private/components';

import { TRANSACTIONS_TYPE } from 'api/transactions/constants';
import {
  IBin,
  ICrossClientStats,
  ICustomer,
  IPaymentProviderCurrency,
  IRoutingRulesetForm,
  ISchedule,
  IStats,
  ITransaction,
  IWeekDaySchedule,
} from 'routes/pages/AuthorizedStack/TrafficManagement/RoutingRuleset/Ruleset/Form/utils/types';
import { IAllPageFilters, INullable, TObject } from 'utils/types';

import { ROUTING_RULESET_OPERATIONS } from './constants';

// === Bulk Copy Rulesets (PCP-32 / CAS-10766) ===

export enum CASHIER_FLAG_MODE {
  ALONG_WITH = 'ALONG_WITH',
  WITHOUT = 'WITHOUT',
}

export enum CASHIER_PM_MODE {
  ALONG_WITH = 'ALONG_WITH',
  TO_EXISTING = 'TO_EXISTING',
  WITHOUT = 'WITHOUT',
}

export enum CROSS_STATS_MODE {
  NONE = 'NONE',
  SAME_AS_SOURCE = 'SAME_AS_SOURCE',
}

export enum TERMINAL_LINK_MODE {
  ALONG_WITH = 'ALONG_WITH',
  TO_EXISTING = 'TO_EXISTING',
  WITHOUT = 'WITHOUT',
}

export type IBaseRulesetData = TObject & {
  cashierPaymentMethod: TObject;
  crossStats: TObject & {
    cumulativeStats: TObject;
    leaderStats: TObject;
    merchantGroup: TObject & {
      merchant: TObject;
    };
  };
  merchantTerminal: TObject & {
    hqLegalEntity: TObject;
    merchant: TObject;
  };
  terminalLink: TObject & {
    bankTerminal: TObject & {
      bank: TObject;
    };
    merchantTerminal: TObject & {
      hqLegalEntity: TObject;
      merchant: TObject;
    };
    parameters: TObject[];
  };
  timerConfig: TObject;
};

export interface IBulkCopyCashierConditional {
  cashierLimitsMode: CASHIER_FLAG_MODE;
  cashierSettingsMode: CASHIER_FLAG_MODE;
}

export interface IBulkCopyCopySettings {
  cashierConditionalSettings?: IBulkCopyCashierConditional;
  cashierPaymentMethodMode: CASHIER_PM_MODE;
  crossStatsMode: CROSS_STATS_MODE;
  terminalLinkAlongWithSettings?: IBulkCopyTerminalLinkAlongSettings;
  terminalLinkMode: TERMINAL_LINK_MODE;
}

export interface IBulkCopyMissingEntity {
  merchantTerminalId: number;
  merchantTerminalName: string;
  missingCashierPaymentMethodNames: string[];
  missingTerminalLinkNames: string[];
}

export interface IBulkCopyRulesetsForm {
  cashierConditionalSettings: IBulkCopyCashierConditional;
  cashierPaymentMethodMode: CASHIER_PM_MODE;
  copiedRulesetEnabled: boolean;
  crossStatsMode: CROSS_STATS_MODE;
  fromMerchantId: INullable<number>;
  fromMerchantTerminalId: INullable<number>;
  fromRoutingSchemaId: INullable<number>;
  rulesetIds: number[];
  terminalLinkAlongWithSettings: IBulkCopyTerminalLinkAlongSettings;
  terminalLinkMode: TERMINAL_LINK_MODE;
  toMerchantTerminalIds: number[];
  toOrganizationId: INullable<number>;
}

export interface IBulkCopyRulesetsPayload {
  copiedRulesetEnabled: boolean;
  copySettings: IBulkCopyCopySettings;
  fromMerchantTerminalId: number;
  rulesetIds: number[];
  toMerchantTerminalIds: number[];
}

export interface IBulkCopyRulesetsResult {
  copiedRulesetCount: number;
  copiedRulesetIdsByTargetMerchant: Record<string, number[]>;
  copySettings: IBulkCopyCopySettings;
  targetMerchantCount: number;
}

// The Copy To section is feature-neutral: any bulk-copy form carrying these two fields can drive it.
export type IBulkCopyTargetsForm = Pick<
  IBulkCopyRulesetsForm,
  'toMerchantTerminalIds' | 'toOrganizationId'
>;

export interface IBulkCopyTerminalLinkAlongSettings {
  withGroupLimits: boolean;
  withOverriddenParameters: boolean;
  withRequiredParameters: boolean;
  withTerminalLinkLimits: boolean;
}

// === Single-target Copy (legacy) ===

export interface ICopyRulesetsForm extends ICopyRulesetsPayload {
  fromMerchantId: INullable<number>;
  fromRoutingSchemaId: INullable<number>;
  toMerchantId: INullable<number>;
}

export interface ICopyRulesetsPayload {
  addCopyPostfix: boolean;
  copyTerminalLinkGroups: boolean;
  fromMerchantTerminalId: INullable<number>;
  rulesetIds: INullable<number[]>;
  toMerchantTerminalId: INullable<number>;
  toRoutingSchemaId: INullable<number>;
}

export interface ICrossStats {
  cumulativeStats: ICrossClientStats['cumulativeStats'];
  id: number;
  leaderStats: ICrossClientStats['leaderStats'];
  merchantGroup: {
    created: string;
    email: boolean;
    firstName: boolean;
    id: number;
    lastName: boolean;
    merchant: {
      id: number;
      name: boolean;
    };
    merchantTerminalIds: number[];
    merchantTerminalNames: string[];
    name: string;
    phone: boolean;
  };
}

export interface IMergeOperationDetail {
  detail: string;
  key: string;
  outcome: string;
  rulesetId: number;
  rulesetName: string;
  section: string;
  skipReason: INullable<string>;
}

export type IMergeOperationDetailData = TObject;

export interface IMergeOperationResult {
  created: number;
  createdAt: string;
  finishedAt: INullable<string>;
  keycloakId: string;
  login: string;
  noOp: number;
  operationId: number;
  searchQuery: string;
  skippedMismatch: number;
  status: string;
  totalMatched: number;
  updated: number;
  validationErrors: number;
}

export type IPeriodsByDays = INullable<Record<string, INullable<IWeekDaySchedule>>>;

export interface IRevertApplyPayload {
  previewToken: string;
}

export interface IRevertApplyResult {
  createdAt: string;
  errorDetail: INullable<string>;
  finishedAt: INullable<string>;
  keycloakId: string;
  login: string;
  operationId: number;
  reverted: number;
  searchQuery: string;
  status: string;
  targetTime: string;
  totalPreviewed: number;
}

export interface IRevertPreviewDiffItem {
  currentValue: INullable<string>;
  field: string;
  newValue: INullable<string>;
}

// ----------------------------------------------------------------

export interface IRevertPreviewEntry {
  diff: IRevertPreviewDiffItem[];
  rulesetId: number;
  rulesetName: string;
}

export interface IRevertPreviewPayload {
  filters: IAllPageFilters;
  includeDeleted: boolean;
  timestamp: string;
}

export interface IRevertPreviewResult {
  entries: IRevertPreviewEntry[];
  historyStartAt: string;
  previewToken: string;
  targetTime: string;
}

export interface IRoutingRuleset extends IRulesetBase {
  billingInfo: boolean;
  binIssuingOrgOperation?: string;
  binIssuingOrgValue: string[];
  binRangeOperation?: string;
  binRangeValue?: string;
  cardNetwork?: string;
  cardType: string[];
  compositeAmount?: string;
  compositeBinCountry?: string;
  compositeBinCountryGroup?: string;
  compositeBinGroup?: string;
  compositeCustomerCountry?: string;
  compositeCustomerCountryGroup?: string;
  compositeCustomerIp?: string;
  compositeTxAmount?: string;
  customerCcDpAmount?: number;
  customerCcDpCount?: number;
  customerCcWdAmount?: number;
  customerCcWdCount?: number;
  customerDaysFromFtd?: number;
  customerDaysFromReg?: number;
  customerDpAmount?: number;
  customerDpCount?: number;
  customerMaxCcDpAmount?: number;
  customerMaxCcDpCount?: number;
  customerMaxCcWdAmount?: number;
  customerMaxCcWdCount?: number;
  customerMaxDpAmount?: number;
  customerMaxDpCount?: number;
  customerMaxUccDpAmount?: number;
  customerMaxUccDpCount?: number;
  customerMaxUccWdAmount?: number;
  customerMaxUccWdCount?: number;
  customerMaxWdAmount?: number;
  customerMaxWdCount?: number;
  customerRegBeforeDate: string;
  customerUccDpAmount?: number;
  customerUccDpCount?: number;
  customerUccWdAmount?: number;
  customerUccWdCount?: number;
  customerWdAmount?: number;
  customerWdCount?: number;
  exchangeConfirmation: boolean;
  exchangeRateAdjustment?: number;
  excludedFromCascading: boolean;
  metadata: IRoutingRulesetMetadata;
  paymentProviderCurrency: string;
  phonePrefixOperation?: string;
  phonePrefixValue?: string;
  routingSchema: { active: boolean; id: INullable<number>; name: INullable<string> };
  schedule: boolean;
  transactionType: TRANSACTIONS_TYPE;
  txCurrency?: string;
  useCurrencyConversion: boolean;
}

export interface IRoutingRulesetBinCountry {
  countryCode: string;
  operation: ROUTING_RULESET_OPERATIONS;
}

export interface IRoutingRulesetBinCountryGroup {
  groupId: number;
  groupName: string;
  operation: ROUTING_RULESET_OPERATIONS;
}

export type IRoutingRulesetData = IBaseRulesetData &
  TObject & {
    binCountry: TObject;
    binCountryGroup: IRoutingRulesetBinCountryGroup;
    binIssuingOrgOperation: string;
    binIssuingOrgValue: string | string[];
    binRange: TObject;
    metadata: IRoutingRulesetMetadata;
    routingSchema: TObject;
  };

export interface IRoutingRulesetMetadata {
  equalsValues?: string;
  exist?: string;
  inValues?: string;
  notEqualsValues?: string;
  notExist?: string;
  notInValues?: string;
}

export interface IRoutingRulesetOperationsData {
  bin: TObject;
  customer: TObject;
  transaction: TObject;
}

export interface IRoutingRulesetOperators {
  bin: {
    binGroup: string[];
    country: string[];
    countryGroup: string[];
    issuingOrg: string[];
    range: string[];
  };
  customer: {
    country: string[];
    countryGroup: string[];
    ip: string[];
    phonePrefix: string[];
  };
  transaction: {
    amount: string[];
    metadata: string[];
    txAmount: string[];
  };
}

export interface IRulesetBase {
  cashierPaymentMethod: INullable<{
    id: number;
    logoData: string;
    merchantId: number;
    merchantName: string;
    merchantTerminalId: number;
    merchantTerminalName: string;
    name: string;
    paymentMethod: string;
  }>;
  closedLoopEnabled: boolean;
  closedLoopTerminalLinks: INullable<number[]>;
  commissionMin: INullable<number>;
  commissionOverride: boolean;
  commissionPct: INullable<number>;
  crossStats: INullable<ICrossStats>;
  enabled: boolean;
  id: number;
  merchantTerminal: {
    apiAccessMode: INullable<string>;
    changePaymentMethod: boolean;
    created: INullable<string>;
    email: INullable<string>;
    enableCascading: boolean;
    enableCashier: boolean;
    hqLegalEntity: INullable<{
      id: number;
      name: string;
    }>;
    id: number;
    merchant: INullable<{
      id: number;
      name: string;
    }>;
    name: string;
    platform: INullable<string>;
    preMemorisedRouting: boolean;
    shopUrl: INullable<string>;
    useCardToken: boolean;
    useRoutingPriority: boolean;
  };
  name: string;
  paymentMethod: INullable<string>;
  priority: INullable<number>;
  ratio: INullable<number>;
  terminalLink: {
    bankTerminal: {
      bank: {
        id: number;
        name: string;
        paymentMethod: string;
      };
      id: number;
      name: string;
    };
    description: INullable<string>;
    enabled: boolean;
    exitIFrame: boolean;
    id: number;
    limitsEnabled: boolean;
    merchantTerminal: INullable<{
      apiAccessMode: string;
      created: string;
      email: string;
      enableCascading: boolean;
      enableCashier: boolean;
      hqLegalEntity: INullable<{
        id: number;
        name: string;
      }>;
      id: number;
      merchant: INullable<{
        id: number;
        name: string;
      }>;
      name: string;
      platform: string;
      preMemorisedRouting: boolean;
      shopUrl: string;
      useCardToken: boolean;
      useRoutingPriority: boolean;
    }>;
    motoEnabled: boolean;
    parameters: INullable<
      {
        dataType: string;
        id: number;
        name: string;
        value: string;
      }[]
    >;
    recurringEnabled: boolean;
    subPaymentMethod: INullable<string>;
    threeDSEnabled: boolean;
    trusted: boolean;
    webhooksEnabled: boolean;
  };
  timerConfig: INullable<{
    failRecoveryPeriod: INullable<number>;
    failRetryCount: INullable<number>;
    failTimerEnabled: boolean;
    successRetryCount: INullable<number>;
    successTimerEnabled: boolean;
    successValidityPeriod: INullable<number>;
  }>;
}

export interface IRulesetsGroupEdit {
  filters: IAllPageFilters;
  // FE-only optimistic patch for the inline-edit grid cache, keyed by entity
  // dataKey (e.g. `{ transactionType }`). Ignored by the API method; the BE
  // payload is built from `settings`. Used because `settings` carries the
  // endpoint's dotted param names (e.g. `stats.dpCount`) which don't match the
  // flat entity fields the grid renders.
  optimisticItem?: TObject;
  settings: TObject;
}

export interface IRulesetsPaymentMethods {
  merchantTerminalId: INullable<number>;
  paymentMethods: ISelectOption[];
}

export type ISingleRoutingRulesetData = TObject & {
  bin: TObject & {
    binGroup: TObject;
    cardBrands: string[];
    cardType: string;
    country: TObject;
    countryGroup: TObject;
    issuingOrg: TObject;
    range: TObject;
  };
  customer: TObject & {
    country: TObject;
    countryGroup: TObject;
    ip: TObject & { countryCodes: string[] };
    phonePrefix: TObject;
  };
  maxStats: TObject;
  paymentProviderCurrency: TObject;
  schedule: TObject & {
    periodsByDays: Record<string, TObject>;
    timeZone: string;
  };
  stats: TObject;
  timer: TObject & {
    failedAttempts: TObject;
    processedAttempts: TObject;
  };
  transaction: TObject & {
    amount: TObject;
    metadata: TObject;
    txAmount: TObject;
    txCcy: string[];
  };
};

export interface ISingleRuleset extends IRulesetBase {
  bin: INullable<IBin>;
  customer: INullable<ICustomer>;
  maxStats: INullable<IStats>;
  paymentProviderCurrency: INullable<IPaymentProviderCurrency>;
  routingSchema: {
    active: boolean;
    id: INullable<number>;
    name: INullable<string>;
  };
  schedule: ISchedule;
  stats: INullable<IStats>;
  transaction: ITransaction;
}

export interface ISingleRulesetData extends IBaseRulesetData {
  bin: ISingleRoutingRulesetData['bin'];
  customer: ISingleRoutingRulesetData['customer'];
  maxStats: ISingleRoutingRulesetData['maxStats'];
  paymentProviderCurrency: TObject;
  routingSchema: TObject;
  schedule: ISingleRoutingRulesetData['schedule'];
  stats: ISingleRoutingRulesetData['stats'];
  transaction: ISingleRoutingRulesetData['transaction'];
}

export interface IUpdateRulesetPayload {
  id: string;
  payload: Omit<IRoutingRulesetForm, 'merchantTerminalId'>;
}

export type TRulesetsPaymentMethodsData = TObject & {
  paymentMethods: TObject;
};
